import { defineStore } from 'pinia';
import {
  fetchOrganizationPlugins,
  fetchPluginApiToken,
  type OrganizationPluginInfo,
} from '@/pages/plugin/plugins';

interface TokenEntry {
  value: string;
  expiresAt: number;
}
interface PluginsEntry {
  items: OrganizationPluginInfo[];
  expiresAt: number;
}

const TOKEN_TTL_MS = 5 * 60 * 1000; // 5 minutes
const PLUGINS_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const usePluginsStore = defineStore('plugins', {
  state: () => ({
    token: null as TokenEntry | null,
    tokenInFlight: null as Promise<string> | null,
    byOrgId: {} as Record<string, PluginsEntry>,
    inFlightByOrgId: {} as Record<string, Promise<OrganizationPluginInfo[]>>,
  }),
  getters: {
    getPlugins:
      (state) =>
      (orgId: string): OrganizationPluginInfo[] | undefined => {
        const e = state.byOrgId[orgId];
        if (!e) return undefined;
        // Return cached items as stored; callers should use `ensurePlugins`
        // to fetch fresh data (expiration is enforced by `ensurePlugins`).
        return e.items;
      },
    getPlugin:
      (state) =>
      (orgId: string, pluginId: string): OrganizationPluginInfo | undefined => {
        const entry = state.byOrgId[orgId];
        if (!entry) return undefined;
        // Return a cached plugin by id. Callers should use `ensurePlugins`
        // to obtain an up-to-date list (expiration is handled there).
        return entry.items.find((p) => p.id === pluginId);
      },
  },
  actions: {
    async ensureToken(): Promise<string> {
      const now = Date.now();
      if (this.token && now < this.token.expiresAt) return this.token.value;
      if (this.tokenInFlight) return this.tokenInFlight;
      this.tokenInFlight = (async () => {
        try {
          const value = await fetchPluginApiToken();
          this.token = { value, expiresAt: now + TOKEN_TTL_MS };
          return value;
        } finally {
          this.tokenInFlight = null;
        }
      })();
      return this.tokenInFlight;
    },

    async ensurePlugins(orgId: string): Promise<OrganizationPluginInfo[]> {
      const now = Date.now();
      const existing = this.byOrgId[orgId];
      if (existing && now < existing.expiresAt) return existing.items;
      const inFlight = this.inFlightByOrgId[orgId];
      if (inFlight) return inFlight;
      const promise = (async () => {
        try {
          const token = await this.ensureToken();
          const items = await fetchOrganizationPlugins(orgId, token);
          this.byOrgId[orgId] = { items, expiresAt: now + PLUGINS_TTL_MS };
          return items;
        } finally {
          delete this.inFlightByOrgId[orgId];
        }
      })();
      this.inFlightByOrgId[orgId] = promise;
      return promise;
    },

    invalidate(orgId?: string) {
      if (orgId) {
        delete this.byOrgId[orgId];
      } else {
        this.byOrgId = {};
      }
    },

    clearAll() {
      this.token = null;
      this.tokenInFlight = null;
      this.byOrgId = {};
      this.inFlightByOrgId = {};
    },
  },
});
